import type { LocationType } from '@shared/core/appointment-types'
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react'
import type { LocationSelectOption } from './location-select.interface'

const ENABLED_OPTION_SELECTOR = '[role="option"]:not(:disabled)'

export function useLocationSelect(onChange: (value: LocationType) => void) {
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const close = useCallback(() => setIsOpen(false), [])

  useDismissOutside(rootRef, isOpen, close)
  useFocusInitialOption(rootRef, isOpen)

  const closeAndRefocus = () => {
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  const select = (option: LocationSelectOption) => {
    if (option.disabled) return
    onChange(option.value)
    closeAndRefocus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && isOpen) {
      event.stopPropagation()
      closeAndRefocus()
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    if (!isOpen) setIsOpen(true)
    else moveOptionFocus(rootRef.current, event.key === 'ArrowDown' ? 1 : -1)
  }

  return { rootRef, triggerRef, isOpen, toggle: () => setIsOpen((open) => !open), select, onKeyDown }
}

function useDismissOutside(rootRef: RefObject<HTMLElement>, isOpen: boolean, onDismiss: () => void) {
  useEffect(() => {
    if (!isOpen) return undefined
    const dismissIfOutside = (event: Event) => {
      if (!rootRef.current?.contains(event.target as Node)) onDismiss()
    }
    document.addEventListener('mousedown', dismissIfOutside)
    document.addEventListener('focusin', dismissIfOutside)
    return () => {
      document.removeEventListener('mousedown', dismissIfOutside)
      document.removeEventListener('focusin', dismissIfOutside)
    }
  }, [rootRef, isOpen, onDismiss])
}

function useFocusInitialOption(rootRef: RefObject<HTMLElement>, isOpen: boolean) {
  useEffect(() => {
    if (!isOpen) return
    const root = rootRef.current
    const selected = root?.querySelector<HTMLElement>(`${ENABLED_OPTION_SELECTOR}[aria-selected="true"]`)
    const target = selected ?? root?.querySelector<HTMLElement>(ENABLED_OPTION_SELECTOR)
    target?.focus()
  }, [rootRef, isOpen])
}

function moveOptionFocus(root: HTMLElement | null, step: 1 | -1) {
  const options = Array.from(root?.querySelectorAll<HTMLElement>(ENABLED_OPTION_SELECTOR) ?? [])
  if (options.length === 0) return
  const current = options.indexOf(document.activeElement as HTMLElement)
  options[(current + step + options.length) % options.length].focus()
}
